import React, { useState, useEffect } from 'react';
import { 
  Lock, 
  LogOut, 
  Users, 
  Phone, 
  Mail, 
  MapPin,
  Calendar,
  Target,
  CheckCircle2,
  Clock,
  UserCheck,
  MessageCircle,
  RefreshCw,
  Search,
  Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Check if already authenticated
  useEffect(() => {
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      verifyToken(savedToken);
    }
  }, []);

  // Verify token
  const verifyToken = async (tokenToVerify) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: tokenToVerify })
      });
      const data = await response.json();
      if (data.valid) {
        setToken(tokenToVerify);
        setIsAuthenticated(true);
        fetchContacts();
      } else {
        localStorage.removeItem('admin_token');
      }
    } catch (error) {
      console.error('Token verification error:', error);
      localStorage.removeItem('admin_token');
    }
  };

  // Login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      
      if (response.ok) {
        const data = await response.json();
        setToken(data.token);
        localStorage.setItem('admin_token', data.token);
        setIsAuthenticated(true);
        toast.success('Connexion réussie');
        fetchContacts();
      } else {
        toast.error('Mot de passe incorrect');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
    setLoading(false);
  };

  // Logout
  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/admin/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    setToken('');
    setContacts([]);
    toast.success('Déconnexion réussie');
  };

  // Fetch contacts
  const fetchContacts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/contact/`);
      if (response.ok) {
        const data = await response.json();
        setContacts(data);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des contacts');
    }
    setLoading(false);
  };

  // Update status
  const updateStatus = async (contactId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/contact/${contactId}/status?status=${newStatus}`, {
        method: 'PATCH'
      });
      
      if (response.ok) {
        toast.success(`Statut mis à jour: ${getStatusLabel(newStatus)}`);
        fetchContacts();
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Get status label
  const getStatusLabel = (status) => {
    switch (status) {
      case 'new': return 'Nouveau';
      case 'contacted': return 'Contacté';
      case 'converted': return 'Converti';
      default: return status;
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'contacted': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'converted': return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  // Filter contacts
  const filteredContacts = contacts.filter(contact => {
    const matchesFilter = filter === 'all' || contact.status === filter;
    const matchesSearch = searchTerm === '' || 
      contact.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.prenom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.telephone?.includes(searchTerm);
    return matchesFilter && matchesSearch;
  });

  // Stats
  const stats = {
    total: contacts.length,
    new: contacts.filter(c => c.status === 'new').length,
    contacted: contacts.filter(c => c.status === 'contacted').length,
    converted: contacts.filter(c => c.status === 'converted').length
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format phone for WhatsApp
  const getWhatsAppLink = (phone) => {
    const cleaned = phone.replace(/\s/g, '').replace(/\./g, '');
    const formatted = cleaned.startsWith('0') ? '33' + cleaned.slice(1) : cleaned;
    return `https://wa.me/${formatted}`;
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full border border-emerald-100">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-emerald-700" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Dashboard Admin</h1>
            <p className="text-gray-600 mt-2">The Hidden Garden EMS</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Input
                type="password"
                placeholder="Mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                data-testid="admin-password-input"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-emerald-700 hover:bg-emerald-800 text-white"
              disabled={loading}
              data-testid="admin-login-btn"
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <a href="/" className="text-emerald-700 hover:text-emerald-800 text-sm">
              ← Retour au site
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                <Users className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Dashboard Admin</h1>
                <p className="text-sm text-gray-500">The Hidden Garden EMS</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button 
                onClick={fetchContacts} 
                variant="outline" 
                size="sm"
                className="flex items-center space-x-2"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Actualiser</span>
              </Button>
              <Button 
                onClick={handleLogout} 
                variant="outline" 
                size="sm"
                className="flex items-center space-x-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Déconnexion</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <Users className="w-10 h-10 text-gray-300" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">Nouveaux</p>
                <p className="text-3xl font-bold text-blue-700">{stats.new}</p>
              </div>
              <Clock className="w-10 h-10 text-blue-200" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-yellow-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-600">Contactés</p>
                <p className="text-3xl font-bold text-yellow-700">{stats.contacted}</p>
              </div>
              <UserCheck className="w-10 h-10 text-yellow-200" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Convertis</p>
                <p className="text-3xl font-bold text-green-700">{stats.converted}</p>
              </div>
              <CheckCircle2 className="w-10 h-10 text-green-200" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Rechercher par nom, email, téléphone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-gray-300"
                data-testid="search-input"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-emerald-500 focus:border-emerald-500"
                data-testid="status-filter"
              >
                <option value="all">Tous les statuts</option>
                <option value="new">Nouveaux</option>
                <option value="contacted">Contactés</option>
                <option value="converted">Convertis</option>
              </select>
            </div>
          </div>
        </div>

        {/* Contacts List */}
        <div className="space-y-4">
          {loading && contacts.length === 0 ? (
            <div className="bg-white p-12 rounded-xl shadow-sm border border-gray-200 text-center">
              <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
              <p className="text-gray-500">Chargement...</p>
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className="bg-white p-12 rounded-xl shadow-sm border border-gray-200 text-center">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Aucun contact trouvé</p>
            </div>
          ) : (
            filteredContacts.map((contact) => (
              <div 
                key={contact.id} 
                className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
                data-testid={`contact-card-${contact.id}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                  {/* Contact Info */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">
                          {contact.prenom} {contact.nom}
                        </h3>
                        <p className="text-sm text-gray-500 flex items-center mt-1">
                          <Calendar className="w-4 h-4 mr-1" />
                          {formatDate(contact.created_at)}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(contact.status)}`}>
                        {getStatusLabel(contact.status)}
                      </span>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center text-gray-600">
                        <Phone className="w-4 h-4 mr-2 text-emerald-600" />
                        <a href={`tel:${contact.telephone}`} className="hover:text-emerald-700">
                          {contact.telephone}
                        </a>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <Mail className="w-4 h-4 mr-2 text-emerald-600" />
                        <a href={`mailto:${contact.email}`} className="hover:text-emerald-700 truncate">
                          {contact.email}
                        </a>
                      </div>
                      {contact.age && (
                        <div className="flex items-center text-gray-600">
                          <Users className="w-4 h-4 mr-2 text-emerald-600" />
                          {contact.age} ans
                        </div>
                      )}
                      {contact.code_postal && (
                        <div className="flex items-center text-gray-600">
                          <MapPin className="w-4 h-4 mr-2 text-emerald-600" />
                          {contact.code_postal}
                        </div>
                      )}
                    </div>

                    {contact.objectif && (
                      <div className="mt-4 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                        <div className="flex items-start">
                          <Target className="w-4 h-4 mr-2 text-emerald-600 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-gray-700">{contact.objectif}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 lg:w-48">
                    <a
                      href={getWhatsAppLink(contact.telephone)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors"
                      data-testid={`whatsapp-btn-${contact.id}`}
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      WhatsApp
                    </a>
                    
                    <div className="border-t border-gray-200 pt-2 mt-2">
                      <p className="text-xs text-gray-500 mb-2 text-center">Changer le statut</p>
                      <div className="flex gap-1">
                        <button
                          onClick={() => updateStatus(contact.id, 'new')}
                          className={`flex-1 px-2 py-1.5 rounded text-xs font-medium transition-colors ${
                            contact.status === 'new' 
                              ? 'bg-blue-600 text-white' 
                              : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                          }`}
                          data-testid={`status-new-${contact.id}`}
                        >
                          Nouveau
                        </button>
                        <button
                          onClick={() => updateStatus(contact.id, 'contacted')}
                          className={`flex-1 px-2 py-1.5 rounded text-xs font-medium transition-colors ${
                            contact.status === 'contacted' 
                              ? 'bg-yellow-600 text-white' 
                              : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                          }`}
                          data-testid={`status-contacted-${contact.id}`}
                        >
                          Contacté
                        </button>
                        <button
                          onClick={() => updateStatus(contact.id, 'converted')}
                          className={`flex-1 px-2 py-1.5 rounded text-xs font-medium transition-colors ${
                            contact.status === 'converted' 
                              ? 'bg-green-600 text-white' 
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                          data-testid={`status-converted-${contact.id}`}
                        >
                          Converti
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
};

export default Admin;
